<script>
    import { fade } from "svelte/transition";
    import { shipmentStore } from "../../stores/store.js";
    import * as api from "../../utiles/api.js";
    import toast from "svelte-french-toast";
    import { Preferences } from "@capacitor/preferences";
    import { onMount } from "svelte";

    let session_bplc_code = "";

    onMount(async () => {
        const { value } = await Preferences.get({ key: "retechInfo" });
        if (value) {
            const userData = JSON.parse(value);
            session_bplc_code = userData.bplc_code || "01";
        }
    });

    export let isLotPopupOpen; // bindable
    export let addSelectedLots;
    export let lotDetails = []; // 💡 추가: 이미 선택된 LOT 목록

    let selectedTargetItem = null;
    let availableLotList = [];
    let isLoading = false;
    let lotsCache = {};

    $: targetItems = ($shipmentStore.details || []).filter(
        (d) => d.delchk === "Y" && d.shipmnt_bnt && Number(d.shipmnt_bnt) > 0,
    );

    function getItemKey(item) {
        if (!item) return "";
        return `${item.item_code}_${item.rcvord_no || ""}_${item.rcvord_sn || ""}`;
    }

    async function fetchLotStock(item) {
        if (selectedTargetItem) {
            lotsCache[getItemKey(selectedTargetItem)] = [...availableLotList];
        }

        selectedTargetItem = item;

        const cacheKey = getItemKey(item);
        if (lotsCache[cacheKey]) {
            availableLotList = lotsCache[cacheKey];
            return;
        }

        availableLotList = []; // 💡 API 호출 전 이전 데이터를 비워 화면 플리커 및 혼선을 방지
        isLoading = true;
        try {
            const res = await api.get(
                `/shipment/selectLotStockList?as_bplc_code=${session_bplc_code}&wrhous_code=${item.wrhous_code}&item_code=${item.item_code}`,
            );
            if (res && res.data) {
                availableLotList = res.data.map((l) => {
                    // 이미 선택된 LOT인지 확인 (수주 번호, 순번 매칭 추가)
                    const existingLot = lotDetails.find(
                        (d) =>
                            d.lot_no === l.lot_no &&
                            d.item_code === item.item_code &&
                            d.rcvord_no === item.rcvord_no &&
                            d.rcvord_sn === item.rcvord_sn,
                    );

                    return {
                        ...l,
                        rcvord_no: item.rcvord_no, // 💡 수주 키값 매칭
                        rcvord_sn: item.rcvord_sn,
                        item_code: item.item_code,
                        delchk: existingLot ? "Y" : "N",
                        input_qy: existingLot ? existingLot.qy : 0,
                    };
                });
            }
        } catch (e) {
            console.error(e);
            toast.error("재고 조회 실패");
        } finally {
            isLoading = false;
        }
    }

    function handleConfirmAll() {
        if (selectedTargetItem) {
            lotsCache[getItemKey(selectedTargetItem)] = [...availableLotList];
        }

        // 💡 품목별로 선택된 총 수량이 지정 출하수량을 초과하는지 사전 검증
        for (const item of targetItems) {
            const itemKey = getItemKey(item);
            const lotList = lotsCache[itemKey] || [];
            
            const totalInputQty = lotList
                .filter((l) => l.delchk === "Y" && l.input_qy > 0)
                .reduce((sum, l) => sum + Number(l.input_qy || 0), 0);
                
            const limitQty = Number(item.shipmnt_bnt || 0);
            
            if (totalInputQty > limitQty) {
                return toast.error(
                    `[${item.item_nm}] 품목의 총 선택 수량(${totalInputQty}개)이 지정된 출하수량(${limitQty}개)보다 많습니다. 수량을 다시 확인해 주세요.`
                );
            }
        }

        let finalSelected = [];
        Object.values(lotsCache).forEach((lotList) => {
            const checked = lotList.filter(
                (l) => l.delchk === "Y" && l.input_qy > 0,
            );
            finalSelected = [...finalSelected, ...checked];
        });

        if (finalSelected.length === 0) {
            return toast.error("선택된 재고 내역이 없습니다.");
        }

        addSelectedLots(finalSelected);
        isLotPopupOpen = false;
    }

    // 반응성 강제 트리거를 위한 카운트 함수
    function getSelectedCount(item, currentList) {
        if (!item) return 0;
        const itemKey = getItemKey(item);
        // 현재 선택된 품목인 경우
        if (selectedTargetItem && getItemKey(selectedTargetItem) === itemKey) {
            return currentList.filter((l) => l.delchk === "Y").length;
        }
        // 캐시에 있는 다른 품목인 경우
        if (lotsCache[itemKey]) {
            return lotsCache[itemKey].filter((l) => l.delchk === "Y").length;
        }
        return 0;
    }

    $: if (isLotPopupOpen && targetItems.length > 0 && !selectedTargetItem) {
        fetchLotStock(targetItems[0]);
    }

    $: if (!isLotPopupOpen) {
        selectedTargetItem = null;
        lotsCache = {};
        availableLotList = [];
    }
</script>

{#if isLotPopupOpen}
    <div class="modal-backdrop" transition:fade>
        <div class="modal-content lot-pop-box">
            <header class="modal-header">
                <div class="header-title">
                    <span class="icon">🔍</span>
                    <h2>재고 LOT 통합 선택기</h2>
                </div>
                <button
                    class="close-x"
                    on:click={() => (isLotPopupOpen = false)}
                >
                    &times;
                </button>
            </header>

            <div class="modal-body split-layout">
                <!-- 왼쪽 패널 -->
                <aside class="left-pane target-section">
                    <div class="section-header">
                        <div class="section-badge teal">품목 리스트</div>
                    </div>
                    <div class="pane-content-scroll">
                        <table class="target-table">
                            <thead>
                                <tr>
                                    <th>품목/규격</th>
                                    <th class="center">선택수</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each targetItems as item}
                                    <tr
                                        class:item-active={selectedTargetItem &&
                                            getItemKey(selectedTargetItem) ===
                                                getItemKey(item)}
                                        on:click={() => fetchLotStock(item)}
                                    >
                                        <td>
                                            <div class="item-cell">
                                                <div class="ord-badge">
                                                    수주: {item.rcvord_no} (순번:
                                                    {item.rcvord_sn})
                                                </div>
                                                <span class="code"
                                                    >{item.item_code}</span
                                                >
                                                <span class="name"
                                                    >{item.item_nm}</span
                                                >
                                                <span class="spec"
                                                    >{item.stndrd}</span
                                                >
                                            </div>
                                        </td>
                                        <td class="center">
                                            <!-- availableLotList를 인자로 넘겨 리액티비티 트리거 -->
                                            <div
                                                class="count-badge"
                                                class:has-val={getSelectedCount(
                                                    item,
                                                    availableLotList,
                                                ) > 0}
                                            >
                                                {getSelectedCount(
                                                    item,
                                                    availableLotList,
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </aside>

                <!-- 오른쪽 패널 -->
                <main class="right-pane table-section">
                    <div class="section-header">
                        <div class="section-badge orange">
                            {#if selectedTargetItem}
                                [{selectedTargetItem.item_nm}] LOT 선택
                            {:else}
                                LOT 선택
                            {/if}
                        </div>
                        <span class="total-text"
                            >조회: {availableLotList.length}건</span
                        >
                    </div>
                    <div class="pane-content-scroll">
                        <table class="mes-table selectable-table">
                            <thead>
                                <tr>
                                    <th class="num">현재고량</th>
                                    <th class="input-header">출하수량</th>
                                    <th>LOT NO</th>
                                    <th>ERP LOT NO</th>
                                    <th>품목코드</th>
                                    <th>품목명</th>
                                    <th>규격</th>
                                    <th>창고</th>
                                </tr>
                            </thead>
                            <tbody>
                                {#each availableLotList as lot, i}
                                    <tr
                                        class:row-selected={lot.delchk === "Y"}
                                        on:click={() => {
                                            lot.delchk =
                                                lot.delchk === "Y" ? "N" : "Y";
                                            if (
                                                lot.delchk === "Y" &&
                                                (!lot.input_qy ||
                                                    lot.input_qy === 0)
                                            ) {
                                                const need = selectedTargetItem
                                                    ? Number(
                                                          selectedTargetItem.shipmnt_bnt,
                                                      ) || 0
                                                    : 0;
                                                lot.input_qy =
                                                    lot.nrmlt_qy > need
                                                        ? need
                                                        : lot.nrmlt_qy;
                                            } else if (lot.delchk === "N") {
                                                lot.input_qy = 0;
                                            }
                                            // Svelte 반응형 강제 트리거
                                            availableLotList = [
                                                ...availableLotList,
                                            ];
                                        }}
                                    >
                                        <td class="num">{lot.nrmlt_qy}</td>
                                        <td
                                            class="input-td"
                                            on:click|stopPropagation
                                        >
                                            <input
                                                type="number"
                                                bind:value={lot.input_qy}
                                                on:focus={(e) =>
                                                    setTimeout(
                                                        () => e.target.select(),
                                                        0,
                                                    )}
                                                on:input={() => {
                                                    if (lot.input_qy > 0)
                                                        lot.delchk = "Y";
                                                    availableLotList = [
                                                        ...availableLotList,
                                                    ];
                                                }}
                                            />
                                        </td>
                                        <td class="highlight-val"
                                            >{lot.lot_no}</td
                                        >
                                        <td class="small-text"
                                            >{lot.erp_lot_no}</td
                                        >
                                        <td>{lot.item_code}</td>
                                        <td>{lot.item_nm}</td>
                                        <td>{lot.stndrd}</td>
                                        <td>{lot.wrhous_code}</td>
                                    </tr>
                                {:else}
                                    <tr>
                                        <td colspan="8" class="empty-row">
                                            {#if isLoading}
                                                데이터 조회 중...
                                            {:else}
                                                리스트가 없습니다.
                                            {/if}
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    </div>
                </main>
            </div>

            <footer class="modal-footer">
                <div class="footer-info">
                    ※ 품목별 선택 내역이 실시간으로 합산됩니다.
                </div>
                <div class="footer-btns">
                    <button
                        class="btn btn-close"
                        on:click={() => (isLotPopupOpen = false)}>취소</button
                    >
                    <button class="btn btn-confirm" on:click={handleConfirmAll}>
                        선택항목 일괄 추가완료
                    </button>
                </div>
            </footer>
        </div>
    </div>
{/if}

<style>
    .modal-backdrop {
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(15, 23, 42, 0.75);
        backdrop-filter: blur(4px);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 3000;
    }

    .lot-pop-box {
        width: 1200px;
        max-width: 95vw;
        height: 750px;
        max-height: 88vh;
        background: white;
        border-radius: 12px;
        box-shadow: 0 20px 40px -10px rgba(0, 0, 0, 0.3);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        border: 1px solid #e2e8f0;
    }

    .modal-header {
        padding: 14px 20px;
        background: #1e293b;
        color: white;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .header-title h2 {
        margin: 0;
        font-size: 1.05rem;
        font-weight: 800;
    }

    .close-x {
        background: none;
        border: none;
        color: #94a3b8;
        font-size: 1.5rem;
        cursor: pointer;
    }

    .modal-body.split-layout {
        flex: 1;
        display: flex;
        padding: 12px;
        gap: 12px;
        background: #f1f5f9;
        overflow: hidden;
    }

    .pane-content-scroll {
        flex: 1;
        overflow: auto;
        background: white;
    }

    .left-pane {
        width: 380px;
        display: flex;
        flex-direction: column;
        gap: 0;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
    }
    .target-table {
        width: 100%;
        border-collapse: separate;
        border-spacing: 0;
    }
    .target-table th {
        background: #f8fafc;
        font-size: 0.72rem;
        padding: 10px;
        border-bottom: 1px solid #e2e8f0;
        font-weight: 800;
    }
    .item-cell {
        display: flex;
        flex-direction: column;
        gap: 1px;
    }
    .ord-badge {
        font-size: 0.65rem;
        background: #e0f2fe;
        color: #0369a1;
        padding: 2px 6px;
        border-radius: 4px;
        font-weight: 700;
        align-self: flex-start;
        margin-bottom: 3px;
        border: 1px solid #bae6fd;
    }
    .item-cell .code {
        font-size: 0.65rem;
        color: #94a3b8;
    }
    .item-cell .name {
        font-size: 0.8rem;
        font-weight: 700;
        color: #1e293b;
    }
    .item-cell .spec {
        font-size: 0.7rem;
        color: #94a3b8;
    }
    .target-table td {
        padding: 10px;
        border-bottom: 1px solid #f1f5f9;
        cursor: pointer;
    }
    .item-active {
        background: #f0fdf4 !important;
        border-left: 4px solid #16a34a !important;
    }

    .count-badge {
        width: 22px;
        height: 22px;
        border-radius: 50%;
        background: #e2e8f0;
        color: #94a3b8;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 0.7rem;
        font-weight: 800;
    }
    .count-badge.has-val {
        background: #22c55e;
        color: white;
    }

    .right-pane {
        flex: 1;
        display: flex;
        flex-direction: column;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        overflow: hidden;
    }
    .mes-table {
        width: 100%;
        min-width: max-content;
        border-collapse: separate;
        border-spacing: 0;
    }
    .mes-table th {
        background: #f8fafc;
        padding: 10px;
        border-bottom: 1px solid #e2e8f0;
        font-size: 0.72rem;
        text-align: left;
        font-weight: 800;
    }
    .mes-table td {
        padding: 10px;
        border-bottom: 1px solid #f8fafc;
        font-size: 0.8rem;
        cursor: pointer;
    }
    .row-selected td {
        background: #eff6ff !important;
    }

    .section-header {
        padding: 8px 12px;
        background: #fff;
        border-bottom: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .section-badge {
        padding: 3px 8px;
        border-radius: 4px;
        font-size: 0.65rem;
        font-weight: 800;
        color: white;
    }
    .section-badge.teal {
        background: #0d9488;
    }
    .section-badge.orange {
        background: #f59e0b;
    }

    .highlight-val {
        font-weight: 700;
        color: #1e293b;
        font-size: 0.85rem;
    }
    .num {
        text-align: right;
        font-family: monospace;
        font-weight: 600;
    }
    .center {
        text-align: center;
    }
    .small-text {
        font-size: 0.7rem;
        color: #94a3b8;
    }

    .input-td {
        background: #fffbeb;
    }
    .input-td input {
        width: 75px;
        height: 26px;
        border: 1px solid #fde68a;
        border-radius: 4px;
        text-align: right;
        padding: 0 5px;
        font-weight: 700;
    }

    .modal-footer {
        padding: 12px 20px;
        background: #fff;
        border-top: 1px solid #e2e8f0;
        display: flex;
        justify-content: space-between;
        align-items: center;
    }
    .footer-info {
        font-size: 0.75rem;
        color: #64748b;
        font-weight: 600;
    }
    .footer-btns {
        display: flex;
        gap: 8px;
    }

    .btn {
        padding: 8px 18px;
        border-radius: 6px;
        font-weight: 800;
        font-size: 0.85rem;
        cursor: pointer;
        border: none;
    }
    .btn-close {
        background: #f1f5f9;
        color: #64748b;
    }
    .btn-confirm {
        background: #3b82f6;
        color: white;
        box-shadow: 0 4px 10px rgba(59, 130, 246, 0.3);
    }

    .empty-row {
        padding: 30px !important;
        text-align: center;
        color: #cbd5e1;
        font-size: 0.85rem;
    }
</style>
